import Button from 'devextreme-react/button';
import { useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useTitle } from '../../../../contexts/title';
import '../../../../css/dashboard.scss';
import { useGetStatistics } from '../../../../api/statistics';
import { useAuth } from '../../../../contexts/auth';
import { useGetCompany } from '../../../../api/company';
import PendingChangeOrdersCard from './PendingChangeOrdersCard';
import { AR, BusinessFlow, InstallerType, ProductLine } from '../../../../types/enums';
import StoreNotesCard from './StoreNotesCard';
import { LoadPagePanel } from '../../../../components';
import { ActionRequiredCard } from '../../../common';
import PostInstallCallCard from './PostInstallCallCard';
import DealerActionRequiredCard from './DealerActionRequiredCard';

export default function CFIDashboard() {
	const navigate = useNavigate();
	const pageTitle = useTitle();

	useEffect(() => {
		pageTitle.setTitle('Action Required Items');
	}, [pageTitle])

	const { user } = useAuth();
	const statistics = useGetStatistics(user?.companyId!, user?.userName!, user?.companyTypeId!);
	const company = useGetCompany(user?.companyId!);

	const deliveryOnlyOrders = !!company.data?.productLines.find(x => x.productLineID === ProductLine.LaminateDelivered ||
		x.productLineCode === 'Small Projects');

	const isLowesBusinessFlow = !!company.data?.assignedRetailers.find(x => x.businessFlowID === BusinessFlow.Lowes);
	const isCountertopInstaller = company.data?.installerTypes.includes(InstallerType.CFI);
	const isCabinetInstaller = company.data?.installerTypes.includes(InstallerType.CCI);
	const isPlumbingInstaller = company.data?.installerTypes.includes(InstallerType.PlumbingInstaller);

	return (
		<>
			<div className='content-block dashboard'>
				<LoadPagePanel visible={statistics.isLoading || statistics.isFetching} />

				{!statistics.isLoading &&
					<div>
						<Button
							type='default'
							text="View in Table"
							icon="Inscyth-Action-Required"
							className='view-in-table-button'
							onClick={() => navigate('/cfi/action-required')}>
						</Button>
						{
							company.data?.SFI &&
							<div className='dashboard-section'>
								<h2>Leads</h2>
								<div className='ar-buttons-container'>
									<ActionRequiredCard
										text='Contact Customer'
										statData={statistics.data}
										statID={10}
										url='/cfi/action-required-orders/10'
										showPending={false}
										showPendingPlaceholder={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Leads: Leads received in the past two business days.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Leads: Leads received more than two business days ago.
												</div>
											</div>
										}
									/>
									<ActionRequiredCard
										text='Schedule Appointment'
										statData={statistics.data}
										statID={20}
										url='/cfi/action-required-orders/20'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Leads: Leads with a "Contact Customer Date" in the past two business days.
												</div>
												<div>
													Pending Leads: Leads that are set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Leads with a "Contact Customer Date" more than two business days ago.
												</div>
											</div>
										}
									/>
									<ActionRequiredCard
										text='Submit Estimate'
										statData={statistics.data}
										statID={30}
										url='/cfi/action-required-orders/30'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Leads: Leads with a "Scheduled Appointment Date" from yesterday and today.
												</div>
												<div>
													Pending Leads: Leads with a "Scheduled Appointment Date" in the future or set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Leads: Leads with a "Scheduled Appointment Date" more than one business day ago.
												</div>
											</div>
										}
									/>
									<ActionRequiredCard
										text='Pending Sale'
										statData={statistics.data}
										statID={40}
										url='/cfi/action-required-orders/40'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Leads: Estimates received in the past one business day.
												</div>
												<div>
													Pending Leads: Estimates that are set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Leads: Estimates received more than one business day ago.
												</div>
											</div>
										}
									/>
									<StoreNotesCard
										statData={statistics.data}
										statID={20000}
										url='/cfi/lead-notes' />
								</div>
							</div>
						}

						<div className='dashboard-section'>
							<h2>Retail Orders</h2>
							<h3>Dates/Schedule &amp; Confirm Order</h3>
							<div className='ar-buttons-container'>
								{
									(isCountertopInstaller || isCabinetInstaller) &&
									<ActionRequiredCard
										text='Contact Customer'
										statData={statistics.data}
										statID={111}
										url='/cfi/action-required-orders/111'
										showPending={false}
										showPendingPlaceholder={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders received in the past two business days.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders received more than two business days ago.
												</div>
											</div>
										}
									/>
								}
								{
									(isCountertopInstaller || isCabinetInstaller) &&
									<ActionRequiredCard
										text={isCountertopInstaller ? 'Enter Ready to Measure/Template Date' : 'Enter Ready to Measure Date'}
										statData={statistics.data}
										statID={112}
										url='/cfi/action-required-orders/112'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders received in the past two business days.
												</div>
												<div>
													Pending Orders: Orders that are set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders received more than two business days ago.
												</div>
											</div>
										}
									/>
								}
								{
									(isCountertopInstaller || isCabinetInstaller) &&
									<ActionRequiredCard
										text={isCountertopInstaller ? 'Enter Measure/Template Date' : 'Enter Measure Date'}
										statData={statistics.data}
										statID={110}
										url='/cfi/action-required-orders/110'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders with a "Ready to Measure/Template Date" of today and tomorrow.
												</div>
												<div>
													Pending Orders: Orders with a "Ready to Measure/Template Date" more than one day in the future or set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders that have already passed the "Ready to Measure/Template Date."
												</div>
											</div>
										}
									/>
								}
								{
									(isCountertopInstaller && isLowesBusinessFlow || isCabinetInstaller) &&
									<ActionRequiredCard
										text='Enter Actual Measure Date'
										statData={statistics.data}
										statID={115}
										url='/cfi/action-required-orders/115'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders with a "Measure Date" of yesterday and today.
												</div>
												<div>
													Pending Orders: Orders with a "Measure Date" in the future or set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders with a "Measure Date" more than one business day ago.
												</div>
											</div>
										}
									/>
								}
								{
									isPlumbingInstaller &&
									<ActionRequiredCard
										text='Schedule Plumbing Disconnect'
										statData={statistics.data}
										statID={116}
										url='/cfi/action-required-orders/116'
										showPending={true}
										showPendingPlaceholder={isCountertopInstaller || isCabinetInstaller}
										hidePastDue={true}
									/>
								}
								{
									isPlumbingInstaller &&
									<ActionRequiredCard
										text='Schedule Plumbing Reconnect'
										statData={statistics.data}
										statID={119}
										url='/cfi/action-required-orders/119'
										showPending={true}
										showPendingPlaceholder={isCountertopInstaller || isCabinetInstaller}
										hidePastDue={true}
									/>
								}
								<ActionRequiredCard
									text={'Submit Change/Confirm'}
									statData={statistics.data}
									statID={120}
									url='/cfi/action-required-orders/120'
									showPending={!isPlumbingInstaller}
									showPendingPlaceholder={isCountertopInstaller || isCabinetInstaller}
									hidePastDue={!isCountertopInstaller && !isCabinetInstaller}
									description={
										(isCountertopInstaller || isCabinetInstaller) ?
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders with a "Template or Actual Measure Date" in the past two business days.<br />
													Delivery only or cabinet orders received today or up to 2 business days.
												</div>
												<div>
													Pending Orders: Orders with a "Template or Actual Measure Date" of today or in the future or set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Countertop orders with a "Template or Actual Measure Date" that is more than two business days ago.
													Delivery only or cabinet orders received 2 or more business days ago.
												</div>
											</div>
											: undefined
									}
								/>
								{
									isCountertopInstaller &&
									<ActionRequiredCard
										text='Validate Change Orders'
										statData={statistics.data}
										statID={AR.CfiValidateChangeOrder}
										url={'/cfi/action-required-orders/' + AR.CfiValidateChangeOrder}
										showPending={false}
										showPendingPlaceholder={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders received in the past two business days in response to a Change order.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders received more than two business days ago in response to a Change order.
												</div>
											</div>
										}
									/>
								}
								{
									(isCountertopInstaller || isCabinetInstaller) &&
									<ActionRequiredCard
										text={deliveryOnlyOrders ? 'Enter Scheduled Install/Delivery Date' : 'Enter Scheduled Install Date'}
										statData={statistics.data}
										statID={143}
										url='/cfi/action-required-orders/143'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders received in the past five business days.
												</div>
												<div>
													Pending Orders: Orders that are set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders received more than five business days ago.
												</div>
											</div>
										}
									/>
								}
								{
									isCabinetInstaller &&
									<ActionRequiredCard
										text={'Verify Cabinet Quality'}
										statData={statistics.data}
										statID={138}
										url='/cfi/action-required-orders/138'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders received in the past five business days.
												</div>
												<div>
													Pending Orders: Orders that are set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders received more than five business days ago.
												</div>
											</div>
										}
									/>
								}
								{
									isCountertopInstaller &&
									<ActionRequiredCard
										text={deliveryOnlyOrders ? 'Enter Actual Install/Delivery Date' : 'Enter Actual Install Date'}
										statData={statistics.data}
										statID={145}
										url='/cfi/action-required-orders/145'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders with a scheduled date of yesterday or today.
												</div>
												<div>
													Pending Orders: Orders with a scheduled date in the future or set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders with a scheduled date more than one business day ago.
												</div>
											</div>
										}
									/>
								}
								{
									isCabinetInstaller &&
									<ActionRequiredCard
										text={'Enter Actual Install Start Date'}
										statData={statistics.data}
										statID={155}
										url='/cfi/action-required-orders/155'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders with a scheduled date of yesterday or today.
												</div>
												<div>
													Pending Orders: Orders with a scheduled date in the future or set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders with a scheduled date more than one business day ago.
												</div>
											</div>
										}
									/>
								}
								{
									isCabinetInstaller &&
									<ActionRequiredCard
										text={'Enter Lower Cabinets Plumb & Level Date'}
										statData={statistics.data}
										statID={156}
										url='/cfi/action-required-orders/156'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders received in the past five business days.
												</div>
												<div>
													Pending Orders: Orders that are set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders received more than five business days ago.
												</div>
											</div>
										}
									/>
								}
								{
									isCabinetInstaller &&
									<ActionRequiredCard
										text={'Enter Actual Install Completion Date'}
										statData={statistics.data}
										statID={157}
										url='/cfi/action-required-orders/157'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders with a scheduled date of yesterday or today.
												</div>
												<div>
													Pending Orders: Orders with a scheduled date in the future or set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders with a scheduled date more than one business day ago.
												</div>
											</div>
										}
									/>
								}								
								{
									isCountertopInstaller &&
									<PendingChangeOrdersCard statData={statistics.data} />
								}
							</div>
							<h3>Documents</h3>
							<div className='ar-buttons-container'>
								{
									isCountertopInstaller && !!company.data?.assignedRetailers.find(x => x.companyCode === 'WREN') &&
									<ActionRequiredCard
										text='Template Checklist'
										statData={statistics.data}
										statID={117}
										url='/cfi/action-required-orders/117'
										showPending={false}
										showPendingPlaceholder={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders received in the past two business days.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders received more than two business days ago.
												</div>
											</div>
										}
									/>
								}
								{
									isCountertopInstaller &&
									<ActionRequiredCard
										text='Upload Post Template Drawing'
										statData={statistics.data}
										statID={AR.CfiUploadPostTemplateDrawing}
										url={'/cfi/action-required-orders/' + AR.CfiUploadPostTemplateDrawing}
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders with a "Measure/Template Date" in the past two business days.
												</div>
												<div>
													Pending Orders: Orders with a "Measure/Template Date" of today and in the future or set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders with a "Measure/Template Date" that is more than two business days ago.
												</div>
											</div>
										}
									/>
								}
								{
									isCountertopInstaller && deliveryOnlyOrders &&
									<ActionRequiredCard
										text='Print Packing Slip'
										statData={statistics.data}
										statID={133}
										url='/cfi/action-required-orders/133'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders with a delivery date of today and up to two business days in the future.
												</div>
												<div>
													Pending Orders: Orders with a delivery date in more than 2 business days in the future.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders with a delivery date in the past.
												</div>
											</div>
										}
									/>
								}
								{
									isCountertopInstaller && !!company.data?.assignedRetailers.find(x => x.companyCode === 'WREN') &&
									<ActionRequiredCard
										text='Installation Photos'
										statData={statistics.data}
										statID={147}
										url='/cfi/action-required-orders/147'
										showPending={false}
										showPendingPlaceholder={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders received in the past two business days.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders received more than two business days ago.
												</div>
											</div>
										}
									/>
								}
								{
									isCountertopInstaller &&
									<ActionRequiredCard
										text={deliveryOnlyOrders ? 'Upload COC/POD' : isLowesBusinessFlow ? 'Upload COC' : 'Upload AOC'}
										statData={statistics.data}
										statID={160}
										url='/cfi/action-required-orders/160'
										showPending={!isPlumbingInstaller}
										showPendingPlaceholder={isCountertopInstaller || isCabinetInstaller}
										hidePastDue={!isCountertopInstaller && !isCabinetInstaller}
										description={
											(isCountertopInstaller || isCabinetInstaller) ?
												<div className='ar-description'>
													<div className='ar-description-active'>
														Active Orders: Orders received in the past two business days.
													</div>
													<div className='ar-description-pastdue'>
														Past Due Orders: Orders received more than two business days ago.
													</div>
												</div>
												: undefined
										}
									/>
								}
								{
									(isCabinetInstaller || isPlumbingInstaller) &&
									<ActionRequiredCard
										text='Upload Work Verification Form'
										statData={statistics.data}
										statID={162}
										url='/cfi/action-required-orders/162'
										showPending={!isPlumbingInstaller}
										showPendingPlaceholder={isCountertopInstaller || isCabinetInstaller}
										hidePastDue={!isCountertopInstaller && !isCabinetInstaller}
										description={
											(isCountertopInstaller || isCabinetInstaller) ?
												<div className='ar-description'>
													<div className='ar-description-active'>
														Active Orders: Orders received in the past two business days.
													</div>
													<div className='ar-description-pastdue'>
														Past Due Orders: Orders received more than two business days ago.
													</div>
												</div>
												: undefined
										}
									/>
								}
								{
									(isCountertopInstaller || isCabinetInstaller) && isLowesBusinessFlow &&
									<PostInstallCallCard
										statData={statistics.data}
										statID={21000}
										url='/cfi/retail-order-post-install' />
								}
								{
									(isCountertopInstaller || isCabinetInstaller) &&
									<StoreNotesCard
										statData={statistics.data}
										statID={20010}
										url='/cfi/retail-order-notes' />
								}
							</div>
						</div>
						{
							isCabinetInstaller &&
							<div className='dashboard-section'>
								<div className='ar-buttons-container'>
									<DealerActionRequiredCard
										text="Dealer to Submit Cabinet Design"
										statData={statistics.data}
										statID={134}
										url='/cfi/action-required-orders/134'
										description="Cabinet Measure Orders on hold waiting for dealer to submit cabinet design packet"
									/>
									<DealerActionRequiredCard
										text="Dealer to Confirm Change Order"
										statData={statistics.data}
										statID={122}
										url='/cfi/action-required-orders/122'
										description="Cabinet Measure Orders on hold waiting for dealer to confirm the change order"
									/>
									<DealerActionRequiredCard
										text="Dealer to Enter Estimated Cabinet Delivery Date"
										statData={statistics.data}
										statID={135}
										url='/cfi/action-required-orders/135'
										description="Cabinet Install Orders on hold waiting for dealer to enter Estimated Cabinet Delivery Date"
									/>
									<DealerActionRequiredCard
										text="Dealer to Enter Cabinet PO Received Date"
										statData={statistics.data}
										statID={137}
										url='/cfi/action-required-orders/137'
										description="Cabinet Install Orders waiting for dealer to enter Cabinet PO Received Date"
									/>
								</div>
							</div>
						}
						{
							(isCountertopInstaller || isCabinetInstaller) &&
							<div className='dashboard-section'>
								<h2>Work Orders</h2>
								<div className='ar-buttons-container'>
									<ActionRequiredCard
										text='Contact Customer'
										statData={statistics.data}
										statID={410}
										url='/cfi/action-required-orders/410'
										showPending={false}
										showPendingPlaceholder={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders received in the past one business day.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders received more than one business day ago.
												</div>
											</div>
										}
									/>
									<ActionRequiredCard
										text='Enter Scheduled Date'
										statData={statistics.data}
										statID={420}
										url='/cfi/action-required-orders/420'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders received in the past two business days.
												</div>
												<div>
													Pending Orders: Orders that are set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders received more than two business days ago.
												</div>
											</div>
										}
									/>
									<ActionRequiredCard
										text='Upload WOC'
										statData={statistics.data}
										statID={440}
										url='/cfi/action-required-orders/440'
										showPending={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders with a "Scheduled Date" in the past two business days.
												</div>
												<div>
													Pending Orders: Orders with a "Scheduled Date" of today or in the future or set as pending.
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders with a "Scheduled Date" more than two business days ago.
												</div>
											</div>
										}
									/>
									<ActionRequiredCard
										text='Enter Work Complete Date'
										statData={statistics.data}
										statID={430}
										url='/cfi/action-required-orders/430'
										showPending={false}
										showPendingPlaceholder={true}
										description={
											<div className='ar-description'>
												<div className='ar-description-active'>
													Active Orders: Orders received in the past two business days.
												</div>
												<div>
												</div>
												<div className='ar-description-pastdue'>
													Past Due Orders: Orders received more than two business days ago.
												</div>
											</div>
										}
									/>
									<StoreNotesCard
										statData={statistics.data}
										statID={20020}
										url='/cfi/work-order-notes' />
								</div>
							</div>
						}
					</div>
				}
			</div>
		</>)
}
